// File: frontend/src/components/admin/primitives/index.js
//
// The admin dashboard's building blocks. These exist because the dashboard was
// 1,750 lines of inline JSX across two files, which meant every contrast bug
// and every missing responsive class had to be fixed in forty places. Fixing
// them here fixes them everywhere.
//
// Colour rule for everything below: semantic tokens only
// (`text-success`, `bg-danger-fill`, `text-ink-light`, `bg-surface-card`).
// Never a literal Tailwind palette shade — those are calibrated for a white
// page and are exactly what made this screen unreadable in dark mode. Never an
// /NN opacity suffix either; the opacity core plugins are disabled for
// Chrome 53 (see tailwind.config.js) and such a class silently emits nothing.
import { useEffect, useRef } from 'react';
import { AlertTriangle, X, ArrowUpRight, ArrowDownRight } from 'lucide-react';

// Tones map to the status tokens. `neutral` is the default surface treatment.
export const TONES = ['neutral', 'success', 'warning', 'danger', 'info', 'accent'];

const TONE_TEXT = {
  neutral: 'text-ink-light',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
  accent: 'text-admin',
};

const TONE_SURFACE = {
  neutral: 'bg-surface-input border-divider',
  success: 'bg-success-fill border-success-border',
  warning: 'bg-warning-fill border-warning-border',
  danger: 'bg-danger-fill border-danger-border',
  info: 'bg-info-fill border-info-border',
  accent: 'bg-admin-fill border-admin-border',
};

export const toneText = (tone) => TONE_TEXT[tone] || TONE_TEXT.neutral;
export const toneSurface = (tone) => TONE_SURFACE[tone] || TONE_SURFACE.neutral;

// ── Card ────────────────────────────────────────────────────────────────────
export function Card({ title, icon: Icon, tone = 'neutral', action, children, className = '', bodyClassName = '' }) {
  return (
    <section className={`bg-surface-card rounded-2xl border border-divider ${className}`}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 px-4 sm:px-6 pt-4 sm:pt-5 pb-3">
          <h3 className="font-semibold text-ink-main flex items-center gap-2 text-sm sm:text-base min-w-0">
            {Icon && <Icon size={18} className={`${toneText(tone)} shrink-0`} aria-hidden="true" />}
            <span className="truncate">{title}</span>
          </h3>
          {action}
        </header>
      )}
      <div className={`px-4 sm:px-6 pb-4 sm:pb-6 ${title ? '' : 'pt-4 sm:pt-6'} ${bodyClassName}`}>
        {children}
      </div>
    </section>
  );
}

// ── StatCard ────────────────────────────────────────────────────────────────
// The single worst offender in the old dashboard: a pastel Tailwind gradient
// (`from-blue-50 to-blue-100`) with the value painted in --color-ink-main.
// In dark mode that is cream on #dbeafe — 1.03:1, i.e. invisible. Seventeen of
// these were on screen.
//
// Now: the card is always a normal surface, and the tone shows up in a small
// icon chip and the trend, never behind the number. The value keeps
// --color-ink-main, which is 14:1 on this surface in both themes.
export function StatCard({
  title, value, subtitle, icon: Icon, tone = 'info', trend, trendValue, hint, onClick,
}) {
  const numeric = typeof value === 'number';
  const body = (
    <>
      <div className="flex items-start justify-between gap-2 mb-3">
        {Icon && (
          <span className={`p-2 rounded-xl border ${toneSurface(tone)} ${toneText(tone)} shrink-0`}>
            <Icon size={18} aria-hidden="true" />
          </span>
        )}
        {trend && trendValue != null && (
          <span
            className={`flex items-center gap-0.5 text-xs font-medium shrink-0 ${
              trend === 'up' ? 'text-success' : 'text-danger'
            }`}
          >
            {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {trendValue}
          </span>
        )}
      </div>
      {/* text-2xl on a phone: at text-3xl a five-digit count wrapped inside a
          160px cell and pushed the label out of the card. */}
      <div className="admin-nums text-2xl sm:text-3xl font-bold text-ink-main leading-tight break-words">
        {value === null || value === undefined ? '—' : numeric ? value.toLocaleString() : value}
      </div>
      <div className="text-sm font-medium text-ink-main mt-1">{title}</div>
      {subtitle && <div className="text-xs text-ink-light mt-1">{subtitle}</div>}
      {hint && <div className="text-xs text-ink-subtle mt-2 leading-snug">{hint}</div>}
    </>
  );

  const shell = 'bg-surface-card border border-divider rounded-2xl p-4 sm:p-5 text-left w-full transition-shadow';
  return onClick
    ? <button type="button" onClick={onClick} className={`${shell} hover:shadow-md`}>{body}</button>
    : <div className={shell}>{body}</div>;
}

// ── Badge ───────────────────────────────────────────────────────────────────
export function Badge({ tone = 'neutral', children, title }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 whitespace-nowrap border ${toneSurface(tone)} ${toneText(tone)}`}
    >
      {children}
    </span>
  );
}

// Security-event severity. `critical` is the one place a solid fill is right —
// it should not look like the other three.
const SEVERITY_TONE = { critical: 'danger', high: 'warning', medium: 'warning', low: 'neutral' };
export function SeverityBadge({ severity }) {
  const solid = severity === 'critical';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide border ${
        solid
          ? 'bg-danger border-danger text-surface-card'
          : `${toneSurface(SEVERITY_TONE[severity] || 'neutral')} ${toneText(SEVERITY_TONE[severity] || 'neutral')}`
      }`}
    >
      {severity}
    </span>
  );
}

// ── Buttons ─────────────────────────────────────────────────────────────────
// min-h-[44px] throughout: the old sort headers and checkboxes were ~32px and
// ~13px, well under a usable touch target.
const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-xl font-medium text-sm min-h-[44px] px-4 transition-colors disabled:opacity-50 disabled:pointer-events-none';

export function Button({ variant = 'secondary', className = '', children, ...rest }) {
  const variants = {
    primary: 'bg-admin text-surface-card hover:opacity-90',
    secondary: 'bg-surface-input text-ink-main hover:bg-[var(--color-hover-surface)]',
    ghost: 'text-ink-light hover:text-ink-main hover:bg-[var(--color-hover-fill)]',
    // --color-hover-danger exists precisely for this and the admin never used
    // it, reaching for hover:bg-red-50 — a near-white flash on charcoal.
    danger: 'border border-danger-border text-danger hover:bg-[var(--color-hover-danger)]',
    dangerSolid: 'bg-danger text-surface-card hover:opacity-90',
  };
  return (
    <button type="button" className={`${BUTTON_BASE} ${variants[variant] || variants.secondary} ${className}`} {...rest}>
      {children}
    </button>
  );
}

// An action whose label collapses below `sm:`, so a toolbar of four of these
// fits a 375px phone instead of overflowing. The label stays in aria-label and
// title, so it is never lost.
export function IconButton({ icon: Icon, label, variant = 'secondary', className = '', children, ...rest }) {
  return (
    <Button
      variant={variant}
      aria-label={label}
      title={label}
      className={`px-0 sm:px-4 w-11 sm:w-auto ${className}`}
      {...rest}
    >
      {Icon && <Icon size={16} aria-hidden="true" />}
      <span className="hidden sm:inline">{children || label}</span>
    </Button>
  );
}

// ── Feedback ────────────────────────────────────────────────────────────────
export function SectionError({ message, onRetry }) {
  if (!message) return null;
  return (
    <div className="bg-danger-fill border border-danger-border text-danger rounded-xl px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-sm min-w-0">
        <AlertTriangle size={16} className="shrink-0" aria-hidden="true" />
        <span className="break-words">{message}</span>
      </div>
      {onRetry && (
        <button onClick={onRetry} className="text-sm font-medium underline underline-offset-2 min-h-[44px] sm:min-h-0">
          Retry
        </button>
      )}
    </div>
  );
}

export function EmptyState({ children, tone = 'neutral' }) {
  return <p className={`text-center py-8 text-sm ${toneText(tone)}`}>{children}</p>;
}

export function Loading({ children = 'Loading…' }) {
  return <p className="text-center py-8 text-sm text-ink-light">{children}</p>;
}

// ── Sheet ───────────────────────────────────────────────────────────────────
// A centred dialog on a desktop, a bottom sheet on a phone.
//
// The old modal was `max-h-[80vh]` inside a fixed layer: vh does not shrink
// when mobile Safari shows its URL bar, so the panel ran off the bottom of the
// screen exactly when the bar was visible. `.admin-sheet` (index.css) declares
// vh then dvh so old browsers keep the first and the rest take the second, and
// the bottom padding clears the home indicator.
export function Sheet({ title, onClose, children, footer, maxWidth = 'max-w-2xl' }) {
  const panel = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Stop the page behind from scrolling while the sheet is open.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div
      className="fixed top-0 left-0 right-0 bottom-0 bg-[var(--color-scrim)] z-50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={`admin-sheet bg-surface-elevated w-full ${maxWidth} rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col outline-none`}
      >
        <header className="px-4 sm:px-6 py-4 border-b border-divider flex items-center justify-between gap-3 shrink-0">
          <h2 className="font-semibold text-ink-main truncate">{title}</h2>
          <Button variant="ghost" onClick={onClose} aria-label="Close" className="px-0 w-11 shrink-0">
            <X size={20} />
          </Button>
        </header>
        <div className="flex-1 overflow-auto px-4 sm:px-6 py-4">{children}</div>
        {footer && (
          <footer className="px-4 sm:px-6 py-3 border-t border-divider shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </footer>
        )}
        {!footer && <div className="pb-[env(safe-area-inset-bottom)] shrink-0" />}
      </div>
    </div>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────
// Two columns from 320px up, one below it.
//
// The old grids were a hard `grid-cols-2` at every width with `text-3xl`
// numbers, so on a narrow phone each card got ~160px and a title like "Avg
// Habits Before Deletion" wrapped to four lines. The fix is not fewer columns
// — one column made every card full-width and turned the Overview tab into a
// very long scroll — it is a shorter title, a smaller number on small screens
// (see StatCard) and a floor below the narrowest phone rather than above it.
export function StatGrid({ children, cols = 4 }) {
  const at = {
    3: 'xs:grid-cols-2 lg:grid-cols-3',
    4: 'xs:grid-cols-2 lg:grid-cols-4',
    5: 'xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-5',
  };
  return <div className={`grid grid-cols-1 ${at[cols] || at[4]} gap-3 sm:gap-4`}>{children}</div>;
}

// A scroll region that is a fraction of the viewport on a phone and a fixed
// height on a desktop. The old `max-h-[32rem]` was taller than an iPhone SE's
// entire viewport, so the page scrolled inside a box that scrolled.
export function ScrollRegion({ children, className = '', desktop = 'sm:max-h-96' }) {
  return <div className={`max-h-[55vh] ${desktop} overflow-auto ${className}`}>{children}</div>;
}

// Label/value pairs that stack on a phone.
export function DefinitionList({ items }) {
  return (
    <dl className="grid grid-cols-1 xs:grid-cols-2 gap-x-4 gap-y-2 text-sm">
      {items.filter(Boolean).map(({ label, value, title }) => (
        <div key={label} className="min-w-0">
          <dt className="text-ink-light text-xs">{label}</dt>
          <dd className="text-ink-main break-words" title={title}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
